import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { ColumnInfo } from '../../../utils/exportUtils';

// Função auxiliar para toLowerCase seguro
const safeToLowerCase = (value: any): string => {
  return String(value || '').toLowerCase();
};

interface ColumnSelectorProps {
  availableColumns: ColumnInfo[];
  selectedColumns: ColumnInfo[];
  onSelectionChange: (columns: ColumnInfo[]) => void;
  loading?: boolean;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  availableColumns,
  selectedColumns,
  onSelectionChange,
  loading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredColumns, setFilteredColumns] = useState<ColumnInfo[]>([]);

  useEffect(() => {
    if (!Array.isArray(availableColumns)) {
      setFilteredColumns([]);
      return;
    }

    const filtered = availableColumns.filter(col => {
      try {
        if (!col || typeof col !== 'object') return false;
        
        // Verificar se as propriedades existem antes de usar toLowerCase
        if (col.name === null || col.name === undefined) return false;
        
        const colName = safeToLowerCase(col.name);
        const colDisplayName = safeToLowerCase(col.displayName);
        const search = safeToLowerCase(searchTerm);
        
        return colName.includes(search) || colDisplayName.includes(search);
      } catch (error) {
        return false;
      }
    });
    
    setFilteredColumns(filtered);
  }, [availableColumns, searchTerm]);

  const handleColumnToggle = (column: ColumnInfo, checked: boolean) => {
    if (checked) {
      // Adicionar coluna se não estiver selecionada
      if (!selectedColumns.find(col => col.name === column.name)) {
        onSelectionChange([...selectedColumns, column]);
      }
    } else {
      // Remover coluna
      onSelectionChange(selectedColumns.filter(col => col.name !== column.name));
    }
  };

  const moveColumn = (index: number, direction: 'up' | 'down') => {
    const newColumns = [...selectedColumns];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < selectedColumns.length) {
      [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
      onSelectionChange(newColumns);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(availableColumns);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleQuickSelect = (type: 'basic' | 'dates' | 'numbers') => {
    let columnsToSelect: ColumnInfo[] = [];
    
    switch (type) {
      case 'basic':
        columnsToSelect = availableColumns.filter(col => 
          ['id', 'name', 'title', 'description'].some(field => 
            safeToLowerCase(col.name).includes(field)
          )
        );
        break;
      case 'dates':
        columnsToSelect = availableColumns.filter(col => 
          col.type === 'date' || safeToLowerCase(col.name).includes('date') ||
          safeToLowerCase(col.name).includes('created') || safeToLowerCase(col.name).includes('updated')
        );
        break;
      case 'numbers':
        columnsToSelect = availableColumns.filter(col => 
          ['number', 'integer', 'decimal', 'float'].includes(safeToLowerCase(col.type))
        );
        break;
    }

    const uniqueColumns = columnsToSelect.filter(col =>
      !selectedColumns.find(selected => selected.name === col.name)
    );

    onSelectionChange([...selectedColumns, ...uniqueColumns]);
  };

  const removeColumn = (columnName: string) => {
    onSelectionChange(selectedColumns.filter(col => col.name !== columnName));
  };

  const isColumnSelected = (columnName: string): boolean => {
    return selectedColumns.some(col => col.name === columnName);
  };

  const renderColumnCheckbox = (column: ColumnInfo) => {
    const isSelected = isColumnSelected(column.name);
    
    return (
      <div className="flex align-items-center p-2">
        <Checkbox
          checked={isSelected}
          onChange={(e) => handleColumnToggle(column, e.checked || false)}
        />
        <div className="ml-2 flex flex-column">
          <span className="font-semibold">{column.displayName || column.name}</span>
          <small className="text-color-secondary">
            {column.type} {column.nullable ? '(Opcional)' : '(Obrigatório)'}
          </small>
        </div>
      </div>
    );
  };

  const renderSelectedColumn = (column: ColumnInfo, index: number) => (
    <div
      key={column.name}
      className="p-2 border-round mb-2 shadow-1 bg-primary-50 border-primary-200"
    >
      <div className="flex align-items-center justify-content-between">
        <div className="flex align-items-center flex-1">
          <span className="mr-2 text-500 font-bold">#{index + 1}</span>
          <div className="flex-1">
            <span className="font-semibold">{column.displayName || column.name}</span>
            <div>
              <small className="text-color-secondary">{column.type}</small>
            </div>
          </div>
        </div>
        
        <div className="flex gap-1">
          <Button
            icon="pi pi-chevron-up"
            className="p-button-text p-button-sm"
            onClick={() => moveColumn(index, 'up')}
            disabled={index === 0}
            tooltip="Mover para cima"
          />
          <Button
            icon="pi pi-chevron-down"
            className="p-button-text p-button-sm"
            onClick={() => moveColumn(index, 'down')}
            disabled={index === selectedColumns.length - 1}
            tooltip="Mover para baixo"
          />
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={() => removeColumn(column.name)}
            tooltip="Remover coluna"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12 md:col-6">
        <Card title="Colunas Disponíveis" className="h-full">
          <div className="mb-3">
            <div className="flex gap-2 mb-2">
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar colunas..."
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button
                label="Todas"
                size="small"
                outlined
                onClick={handleSelectAll}
                disabled={loading}
              />
              <Button
                label="Limpar"
                size="small"
                outlined
                severity="secondary"
                onClick={handleClearAll}
                disabled={loading}
              />
              <Button
                label="Básicas"
                size="small"
                outlined
                severity="info"
                onClick={() => handleQuickSelect('basic')}
                disabled={loading}
              />
              <Button
                label="Datas"
                size="small"
                outlined
                severity="warning"
                onClick={() => handleQuickSelect('dates')}
                disabled={loading}
              />
              <Button
                label="Números"
                size="small"
                outlined
                severity="success"
                onClick={() => handleQuickSelect('numbers')}
                disabled={loading}
              />
            </div>
          </div>

          <div className="overflow-auto" style={{ maxHeight: '400px' }}>
            {loading ? (
              <div className="text-center p-4">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                <p>Carregando colunas...</p>
              </div>
            ) : (
              filteredColumns.map(column => (
                <div key={column.name}>
                  {renderColumnCheckbox(column)}
                </div>
              ))
            )}
          </div>

          <Divider />
          <div className="text-center">
            <small className="text-color-secondary">
              {filteredColumns.length} colunas disponíveis
            </small>
          </div>
        </Card>
      </div>

      <div className="col-12 md:col-6">
        <Card title={`Colunas Selecionadas (${selectedColumns.length})`} className="h-full">
          <div className="mb-3">
            <div className="flex justify-content-between align-items-center">
              <span className="text-sm text-color-secondary">
                Arraste para reordenar as colunas
              </span>
              {selectedColumns.length > 0 && (
                <Chip 
                  label={`${selectedColumns.length} selecionadas`} 
                  className="bg-primary text-white"
                />
              )}
            </div>
          </div>

          {selectedColumns.length === 0 ? (
            <div className="text-center p-4 text-color-secondary">
              <i className="pi pi-info-circle mb-2" style={{ fontSize: '2rem' }} />
              <p>Nenhuma coluna selecionada</p>
              <small>Selecione colunas da lista à esquerda</small>
            </div>
          ) : (
            <div className="overflow-auto" style={{ maxHeight: '400px' }}>
              {selectedColumns.map((column, index) => 
                renderSelectedColumn(column, index)
              )}
            </div>
          )}

          {selectedColumns.length > 0 && (
            <>
              <Divider />
              <div className="text-center">
                <small className="text-color-secondary">
                  Ordem da exportação: {selectedColumns.map(col => col.displayName || col.name).join(' → ')}
                </small>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};